import { createContext, useContext, useState, useEffect, ReactNode } from "react";
import { Lang, LANG_LABELS, t, TranslationKey } from "@/i18n/translations";
import { useUserPreferences, useUpdatePreferences } from "@/hooks/useSupabaseData";
import { useAuth } from "@/contexts/AuthContext";

const ALL_LANGS: Lang[] = ["en", "es", "zh", "hi", "ar", "pt", "fr", "ru", "ja", "de", "ko"];

interface LanguageContextType {
  lang: Lang;
  setLang: (l: Lang) => void;
  t: (key: TranslationKey) => string;
  allLangs: Lang[];
  langLabel: (l: Lang) => string;
}

const LanguageContext = createContext<LanguageContextType>({
  lang: "en",
  setLang: () => {},
  t: (key) => key,
  allLangs: ALL_LANGS,
  langLabel: (l) => l,
});

export const useLanguage = () => useContext(LanguageContext);

export const LanguageProvider = ({ children }: { children: ReactNode }) => {
  const [lang, setLangState] = useState<Lang>(() => (localStorage.getItem("lang") as Lang) || "en");
  const { user } = useAuth();
  const { data: prefs } = useUserPreferences();
  const updatePrefs = useUpdatePreferences();

  useEffect(() => {
    if (prefs?.language && ALL_LANGS.includes(prefs.language as Lang)) {
      setLangState(prefs.language as Lang);
    }
  }, [prefs?.language]);

  const setLang = (l: Lang) => {
    setLangState(l);
    localStorage.setItem("lang", l);

    // Persist only when authenticated (auth pages should still allow local-only language selection)
    if (user) {
      updatePrefs.mutate({ language: l });
    }
  };

  // RTL support for Arabic
  useEffect(() => {
    const dir = lang === "ar" ? "rtl" : "ltr";
    document.documentElement.dir = dir;
    document.documentElement.lang = lang;
  }, [lang]);

  const translate = (key: TranslationKey) => t(lang, key);
  const langLabel = (l: Lang) => LANG_LABELS[l] || l;

  return (
    <LanguageContext.Provider value={{ lang, setLang, t: translate, allLangs: ALL_LANGS, langLabel }}>
      {children}
    </LanguageContext.Provider>
  );
};
